import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { StationsTableProps, StationV2 } from '../../../types/dashboardV2';
import { getStatusBadgeClass, getStatusDotClass, formatStatus } from '../../../shared/utils/statusUtils';
import { ROUTES } from '../../../constants/routes';
import { formatUAH } from '../../../shared/utils/currency';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { StatusBadgeCell, TableActionButton } from '../components/cells';
import { usePrivacy } from '../../../context/PrivacyContext';
import { MASK } from '../../../shared/utils/mask';

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const STATIONS_TABLE_MIN_WIDTH = '1060px';

export const STATIONS_COLUMNS = [
  { key: 'idTag', labelKey: 'dashboard.tables.stations.idTag', width: '15%', cellClassName: 'cell-id', skeletonVariant: 'id' },
  { key: 'option', labelKey: 'dashboard.tables.stations.option', width: '6.5%', align: 'center', cellClassName: 'cell-option', tabularNums: true, skeletonVariant: 'option' },
  { key: 'status', labelKey: 'dashboard.tables.stations.status', width: '12%', skeletonVariant: 'status' },
  { key: 'price', labelKey: 'dashboard.tables.stations.price', width: '8.5%', align: 'center', cellClassName: 'cell-price', tabularNums: true, skeletonVariant: 'price' },
  { key: 'connectors', labelKey: 'dashboard.tables.stations.connectors', width: '8%', align: 'center', cellClassName: 'cell-connectors', tabularNums: true, skeletonVariant: 'conn' },
  { key: 'address', labelKey: 'dashboard.tables.stations.address', width: '26%', cellClassName: 'cell-address', skeletonVariant: 'address' },
  { key: 'organization', labelKey: 'dashboard.tables.stations.organization', width: '18.5%', skeletonVariant: 'id' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '5.5%', align: 'right', skeletonVariant: 'action' },
] as const satisfies readonly DataTableColumnMeta[];

export const StationsTable = memo(function StationsTable({
  stations,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onQrClick,
}: StationsTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRowClick = useCallback(
    (station: StationV2) => {
      navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_STATIONS}/${station.id}`);
    },
    [navigate],
  );

  const { hidden } = usePrivacy('stations');

  const renderers = useMemo<DataTableRenderers<StationV2, typeof STATIONS_COLUMNS>>(
    () => ({
      idTag: (s) => s.idTag,
      option: (s) => s.option,
      status: (s) => (
        <StatusBadgeCell
          badgeClass={getStatusBadgeClass(s.status)}
          dotClass={getStatusDotClass(s.status)}
          label={formatStatus(s.status)}
        />
      ),
      price: (s) => (hidden ? MASK : formatUAH(s.price)),
      connectors: (s) => s.connectors,
      address: (s) => s.address,
      organization: (s) => s.organizationName ?? <span className="org-text-muted">—</span>,
      actions: (s) => (
        <TableActionButton
          icon="qr_code_2"
          className="qr-btn"
          onClick={() => onQrClick(s)}
          ariaLabel={t('dashboard.tables.downloadQr', { id: s.idTag })}
          stopPropagation
        />
      ),
    }),
    [t, onQrClick, hidden],
  );

  return (
    <DataTable
      columns={STATIONS_COLUMNS}
      renderers={renderers}
      rows={stations}
      rowKey={(s) => s.id}
      minWidth={STATIONS_TABLE_MIN_WIDTH}
      dense
      pagination={{ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }}
      onRowClick={handleRowClick}
      rowAriaLabel={(s) => t('dashboard.tables.viewStation', { id: s.idTag })}
    />
  );
});

export default StationsTable;
